import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./index.css"
import { useAtom, useSetAtom } from "jotai";
import { ToastContainer, Flip } from "react-toastify";
import { lazy, useEffect } from "react";
import React from "react";
import { Suspense } from 'react';
import { userAtom } from "./store/atoms";
import { adminEmails } from "./utils/format";
import ScrollToTop from "./components/common/ScrollTop";
import BackToTop from "./components/common/BackToTop";
import { jwtDecode } from "jwt-decode";
import { User } from "./types";
import CertificateDetail from "./views/CertificateDetail";
import AiCompanion from "./views/AiCompanion";
import LoadingSpinner from "./components/common/LoadingSpinner";
import ErrorBoundary from "./components/common/ErrorBoundary";
import TradingTool from "./views/TradingTool";
import SymbolsTradingHoursView from "./views/SymbolsTradingHoursView";
import TradingViewToolView from "./views/TradingViewToolView";
import TimezoneConverterView from "./views/TimezoneConverterView";
import LotSizeCalculatorView from "./views/LotSizeCalculatorView";
import EquitySimulatorView from "./views/EquitySimulatorView";
// import ChatButton from "./components/common/ChatButton";
import AiButton from "./components/common/AIButton";
import KYCProtectedRoute from "./components/auth/KYCProtectedRoute";
import RoleProtectedRoute from "./components/auth/RoleProtectedRoute";
import { UserDefaultRedirect, AdminDefaultRedirect } from "./components/auth/RoleBasedRedirects";
import FinAgent from "./components/common/Agent";
import TrustAdvisorWidget from "./components/common/TrustAdvisorWidget";
import { auth } from "./api/endpoints/auth";
import Faqs from "./components/page/Faqs";
import SalesPage from "./components/page/Programs.tsx";

let sessionBootstrapPromise: Promise<void> | null = null;

/** WordPress lives at /blogs — send /blog (and stray SPA hits on /blogs) there instead of home. */
function ExternalBlogRedirect() {
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    let targetPath = pathname;

    if (pathname === "/blog" || pathname.startsWith("/blog/")) {
      targetPath = pathname.replace(/^\/blog(?=\/|$)/, "/blogs");
    }

    if (!targetPath.endsWith("/") && !/\.[a-zA-Z0-9]+$/.test(targetPath)) {
      targetPath = `${targetPath}/`;
    }

    window.location.replace(`https://urfx.io${targetPath}${search}${hash}`);
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-black text-white">
      <p className="text-sm text-white/70">Redirecting to blog…</p>
    </div>
  );
}

// Lazy loaded components
const LandingView = lazy(() => import("./views/LandingView"));
const DashboardView = lazy(() => import("./views/DashboardView"));
const AdminViewNew = lazy(() => import("./views/AdminViewNew"));
const Layout = lazy(() => import("./layout"));
const SignupView = lazy(() => import("./views/SignupView"));
const SigninView = lazy(() => import("./views/SigninView"));
const AdminDashboard = lazy(() => import("./components/admin/AdminDashboard"));
const UserAccountTable = lazy(
  () => import("./components/admin/UserAccountTable")
);
const DiscountedAccountsTable = lazy(
  () => import("./components/admin/DiscountedAccountsTable")
);
const PaymentTable = lazy(
  () => import("./components/admin/PaymentTable")
);
const PoolAccountsTable = lazy(
  () => import("./components/admin/PoolAccountsTable")
);
const AdminPayoutsView = lazy(() => import("./views/AdminPayoutsView"));
const AdminUserAssignmentView = lazy(() => import("./views/AdminUserAssignmentView"));
const AdminCouponsView = lazy(() => import("./views/AdminCouponsView"));
const AdminPromoCardsView = lazy(() => import("./views/AdminPromoCardsView"));
const AdminSubscribersView = lazy(() => import("./views/AdminSubscribersView"));
const AdminInactiveAccountsView = lazy(() => import("./views/AdminInactiveAccountsView"));
const AdminTerminatedAccountsView = lazy(() => import("./views/AdminTerminatedAccountsView"));
const AdminSupportTicketsView = lazy(() => import("./views/AdminSupportTicketsView"));
const AdminFinSupportView = lazy(() => import("./views/AdminFinSupportView"));
const AdminCertificatesView = lazy(() => import("./views/AdminCertificatesView"));
const AdminContractsView = lazy(() => import("./views/AdminContractsView"));
const AdminPerformanceView = lazy(() => import("./views/AdminPerformanceView"));
const AdminPopupConfigView = lazy(() => import("./views/AdminPopupConfigView"));
const AdminPromoCodeLandingPageView = lazy(() => import("./views/AdminPromoCodeLandingPageView"));
const AdminDashboardPromoCardView = lazy(() => import("./views/AdminDashboardPromoCardView"));
const AdminTradesView = lazy(() => import("./views/AdminTradesView"));
const AdminLeaderboardView = lazy(() => import("./views/AdminLeaderboardView"));
const AdminUserActivitiesView = lazy(() => import("./views/AdminUserActivitiesView"));
const AdminTradingRulesView = lazy(() => import("./views/AdminTradingRulesView"));
const AdminKYCView = lazy(() => import("./views/AdminKYCView"));
const AdminBannerConfigView = lazy(() => import("./views/AdminBannerConfigView"));
const AdminAccountBannerConfigView = lazy(() => import("./views/AdminAccountBannerConfigView"));
const AdminExtendedBannerConfigView = lazy(() => import("./views/AdminExtendedBannerConfigView"));
const AdminPaymentDiscountView = lazy(() => import("./views/AdminPaymentDiscountView"));
const AdminPaytikoSettingsView = lazy(() => import("./views/AdminPaytikoSettingsView"));
const AdminUsersView = lazy(() => import("./views/AdminUsersView"));
const AdminCustomerProfileView = lazy(() => import("./views/AdminCustomerProfileView"));
const AdminUserRolesView = lazy(() => import("./views/AdminUserRolesView"));
const AdminSignaturesView = lazy(() => import("./views/AdminSignaturesView"));
const AdminBalanceCheckView = lazy(() => import("./views/AdminBalanceCheckView"));
const AdminVpsView = lazy(() => import("./views/AdminVpsView.tsx"));
const AdminNexcoreVpsView = lazy(() => import("./views/AdminNexcoreVpsView.tsx"));
const AdminNexcoreVpsCustomersView = lazy(() => import("./views/AdminNexcoreVpsCustomersView.tsx"));
const VpsAccountsTable = lazy(() => import("./components/admin/VpsAccountsTable"));

const MTAccounts = lazy(() => import("./pages/admin/MTAccounts"));
const MetaApiTokens = lazy(() => import("./pages/admin/MetaApiTokens"));
const TradeMateUserAccountTable = lazy(() => import("./components/admin/TradeMateUserAccountTable"));
const TradeMateAccountsTable = lazy(() => import("./components/admin/TradeMateAccountsTable"));
const UserLayout = lazy(() => import("./layout/UserLayout"));
const ComingSoonView = lazy(() => import("./views/ComingSoonView"));
const ProfileSettingView = lazy(() => import("./views/ProfileSettingView"));
const KYCView = lazy(() => import("./views/KYCView"));
const UserTradingAccounts = lazy(
  () => import("./components/user/UserTradingAccounts")
);
const NewAccountView = lazy(() => import("./views/NewAccountView"));
const BillingTable = lazy(() => import("./components/user/BillingTable"));
const PaymentSuccess = lazy(() => import("./components/PaymentSuccess"));
// const ContactUs = lazy(() => import("./components/page/ContactUs"));
const AboutUs = lazy(() => import("./components/page/AboutUs"));
const SupportPage = lazy(() => import("./components/page/Support"));
// const FAQ = lazy(() => import("./components/page/FAQ"));

const TermsAndConditions = lazy(
  () => import("./components/page/TermsAndConditions")
);
const PrivacyAndPolicy = lazy(() => import("./components/page/PrivacyPolicy"));
const BecomePartner = lazy(() => import("./components/page/BecomeaPartner"));
const TradingRules = lazy(() => import("./components/page/TradingRules"));
const Leaderboard = lazy(() => import("./components/user/Leaderboard"));
const ForgotPasswordView = lazy(() => import("./views/ForgotPasswordView"));
const ResetPasswordView = lazy(() => import("./views/ResetPasswordView"));
const Pricing = lazy(() => import("./views/Pricing"));
const CertificatesView = lazy(() => import("./views/CertificatesView"));
const AnalyticsView = lazy(() => import("./views/AnalyticsView"));
const Checkout = lazy(() => import("./components/checkout/Checkout"));
const VerifyEmailView = lazy(() => import("./views/EmailVerifyView"));
const CongratForSignup = lazy(() => import("./views/CongratForSignUp"));
const CalendarNews = lazy(() => import("./components/user/CalendarNews"));
const AdminDashboardView = lazy(() => import("./views/AdminDashboardView"));
const PerformanceView = lazy(() => import("./views/PerformanceView"));
const TestimonialView = lazy(() => import("./views/TestimonialView"));
const AdminAcademyView = lazy(() => import("./views/AdminAcademyView"));
const PromoView = lazy(() => import("./views/PromoView"));
const CertificateVerficationView = lazy(() => import("./views/CertificateVerficationView"));
const ESignView = lazy(() => import("./views/ESignView"));


function AppContent() {
  const location = useLocation();
  const setUserInfoGlobal = useSetAtom(userAtom);

  // Don't show chat button on academy page
  // const showChatButton = !location.pathname.includes('/academy');
  const showAiButton = location.pathname.includes('/academy');
  // Restore session once, then hydrate via /auth/me for a single source of truth.
  React.useEffect(() => {
    const userToken = localStorage.getItem("token");
    if (userToken) {
      try {
        // 1) Fast path: hydrate from localStorage / JWT so UI can render immediately.
        const storedUserData = localStorage.getItem("userData");
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          setUserInfoGlobal(userData);
        } else {
          // Fallback to JWT token data
          const tokenData = jwtDecode<User>(userToken);
          setUserInfoGlobal(tokenData);
        }
      } catch (error) {
        // Fallback to JWT token
        try {
          const tokenData = jwtDecode<User>(userToken);
          setUserInfoGlobal(tokenData);
        } catch (tokenError) {
          localStorage.removeItem("token");
          localStorage.removeItem("userData");
          setUserInfoGlobal(null);
        }
      }

      // 2) Authoritative fetch: call /auth/me once per app load and update global user.
      if (!sessionBootstrapPromise) {
        sessionBootstrapPromise = (async () => {
          try {
            const res = await auth.me();
            const me = (res?.data ?? res?.user ?? res) as Partial<User> | undefined;
            if (me && (me as any).email) {
              localStorage.setItem("userData", JSON.stringify(me));
              setUserInfoGlobal(me as User);
            }
          } catch (e: any) {
            const status = e?.response?.status;
            if (status === 401 || status === 403) {
              localStorage.removeItem("token");
              localStorage.removeItem("userData");
              localStorage.removeItem("userRole");
              localStorage.removeItem("userRoleId");
              setUserInfoGlobal(null);
            }
          } finally {
            sessionBootstrapPromise = null;
          }
        })();
      }
    }
  }, [setUserInfoGlobal]);

  function ProtectedAdminAuthRoute({ children }: { children: any }) {
    const [user] = useAtom(userAtom);

    // Check if user has a valid token
    const token = localStorage.getItem("token");

    // If no token, redirect to login
    if (!token) {
      return <Navigate to="/login" />;
    }

    // If token exists but user state not loaded yet, show loading or wait
    if (!user?.email) {
      // Token exists but user state not loaded, allow access (user data will load from useEffect)
      return <>{children}</>;
    }

    // Check if the user is logged in and if their email is in the adminEmails list
    if (user?.email && adminEmails.includes(user.email)) {
      return <>{children}</>;
    }

    // Redirect non-admin users to the home page
    return <Navigate to="/" />;
  }

  function ProtectedUserAuthRoute({ children }: { children: any }) {
    const [user] = useAtom(userAtom);

    // Check if user has a valid token
    const token = localStorage.getItem("token");

    // If no token, redirect to login
    if (!token) {
      return <Navigate to="/login" />;
    }

    // If token exists but user state not loaded yet, show loading or continue
    if (!user?.email) {
      // Token exists but user state not loaded, allow access
      return <>{children}</>;
    }

    // Allow access for all logged-in users (both regular users and admins)
    if (user?.email) {
      return <>{children}</>;
    }

    // This shouldn't be reached, but fallback to login
    return <Navigate to="/login" />;
  }

  return (
    <>
      {/* {showChatButton && <ChatButton />} */}
      {showAiButton && <AiButton />}
      <ScrollToTop />
      <BackToTop />
      <FinAgent />
      <TrustAdvisorWidget />
      <div className="bg-[#070707] font-creato">
        <main className={`transition-all duration-300 w-full`}>
          <div className="max-w-full mx-auto">
            <Routes>
              {/* //Admin Routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedAdminAuthRoute>
                    <AdminViewNew />
                  </ProtectedAdminAuthRoute>
                }
              >
                <Route
                  path=""
                  element={<AdminDefaultRedirect />}
                />
                <Route
                  path="dashboard"
                  element={
                    <AdminDashboard />
                  }
                />
                <Route
                  path="useraccount"
                  element={
                    <UserAccountTable />
                  }
                />
                <Route
                  path="discounted-accounts"
                  element={
                    <DiscountedAccountsTable />
                  }
                />
                <Route
                  path="useractivities"
                  element={
                    <AdminUserActivitiesView />
                  }
                />
                <Route
                  path="trading-rules"
                  element={
                    <AdminTradingRulesView />
                  }
                />
                <Route
                  path="payments"
                  element={
                    <PaymentTable />
                  }
                />
                <Route
                  path="pool-accounts"
                  element={
                    <PoolAccountsTable />
                  }
                />
                <Route
                  path="payouts"
                  element={
                    <AdminPayoutsView />
                  }
                />
                <Route
                  path="user-assignment"
                  element={
                    <AdminUserAssignmentView />
                  }
                />
                <Route
                  path="coupons"
                  element={
                    <AdminCouponsView />
                  }
                />
                <Route
                  path="promo-cards"
                  element={
                    <AdminPromoCardsView />
                  }
                />
                <Route
                  path="subscribers"
                  element={
                    <AdminSubscribersView />
                  }
                />
                <Route
                  path="inactive-accounts"
                  element={
                    <AdminInactiveAccountsView />
                  }
                />
                <Route
                  path="terminated-accounts"
                  element={
                    <AdminTerminatedAccountsView />
                  }
                />
                <Route
                  path="vps"
                  element={
                    <AdminVpsView />
                  }
                />
                <Route
                  path="terminals"
                  element={
                    <VpsAccountsTable />
                  }
                />
                <Route
                  path="nexcore-vps"
                  element={
                    <AdminNexcoreVpsView />
                  }
                />
                <Route
                  path="nexcore-vps-customers"
                  element={
                    <AdminNexcoreVpsCustomersView />
                  }
                />
                <Route
                  path="support-tickets"
                  element={
                    <AdminSupportTicketsView />
                  }
                />
                <Route
                  path="fin-support"
                  element={
                    <AdminFinSupportView />
                  }
                />
                <Route
                  path="certificates"
                  element={
                    <AdminCertificatesView />
                  }
                />
                <Route
                  path="contracts"
                  element={
                    <AdminContractsView />
                  }
                />
                <Route
                  path="trades"
                  element={
                    <AdminTradesView />
                  }
                />
                <Route
                  path="performance"
                  element={
                    <AdminPerformanceView />
                  }
                />
                <Route
                  path="popup-config"
                  element={
                    <AdminPopupConfigView />
                  }
                />
                <Route
                  path="promo-code-landing-page"
                  element={
                    <AdminPromoCodeLandingPageView />
                  }
                />
                <Route
                  path="dashboard-promo-card"
                  element={
                    <AdminDashboardPromoCardView />
                  }
                />
                <Route
                  path="leaderboard"
                  element={
                    <AdminLeaderboardView />
                  }
                />
                <Route
                  path="kyc"
                  element={
                    <AdminKYCView />
                  }
                />
                <Route
                  path="balance-check"
                  element={
                    <AdminBalanceCheckView />
                  }
                />
                <Route
                  path="banner-config"
                  element={
                    <AdminBannerConfigView />
                  }
                />
                <Route
                  path="account-banner-config"
                  element={
                    <AdminAccountBannerConfigView />
                  }
                />
                <Route
                  path="extended-banner-config"
                  element={
                    <AdminExtendedBannerConfigView />
                  }
                />
                <Route
                  path="payment-discount"
                  element={
                    <AdminPaymentDiscountView />
                  }
                />
                <Route
                  path="paytiko-settings"
                  element={
                      <AdminPaytikoSettingsView />
                  }
                />
                <Route
                  path="users"
                  element={
                    <AdminUsersView />
                  }
                />
                <Route
                  path="users/:userId/details"
                  element={
                    <AdminCustomerProfileView />
                  }
                />
                <Route
                  path="user-roles"
                  element={
                    <AdminUserRolesView />
                  }
                />
                <Route
                  path="signatures"
                  element={
                    <AdminSignaturesView />
                  }
                />

                {/* Trading accounts — one table, filtered by MetaApi token */}
                <Route
                  path="mtaccounts"
                  element={
                    <MTAccounts />
                  }
                />
                <Route
                  path="metaapi-tokens"
                  element={
                    <MetaApiTokens />
                  }
                />

                {/* Trademate Admin routes */}
                <Route
                  path="trademate-user-accounts"
                  element={
                    <TradeMateUserAccountTable />
                  }
                />
                <Route
                  path="trademate-accounts"
                  element={
                    <TradeMateAccountsTable />
                  }
                />
              </Route>

              {/*User routes */}
              <Route
                path="/user"
                element={
                  <ProtectedUserAuthRoute>
                    <KYCProtectedRoute>
                      <UserLayout />
                    </KYCProtectedRoute>
                  </ProtectedUserAuthRoute>
                }
              >
                <Route path="" element={<UserDefaultRedirect />} />

                <Route path="dashboard" element={<RoleProtectedRoute pageId="dashboard"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><DashboardView /></Suspense></ErrorBoundary></RoleProtectedRoute>} />
                <Route path="billing" element={<RoleProtectedRoute pageId="billing"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><BillingTable /></Suspense></ErrorBoundary></RoleProtectedRoute>} />
                <Route
                  path="account"
                  element={
                    <RoleProtectedRoute pageId="accounts"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><UserTradingAccounts /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="account/new-account"
                  element={
                    <RoleProtectedRoute pageId="accounts"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><NewAccountView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="profilesetting"
                  element={
                    <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ProfileSettingView /></Suspense></ErrorBoundary>
                  }
                />
                <Route
                  path="kyc"
                  element={
                    <RoleProtectedRoute pageId="kyc"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><KYCView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="esign"
                  element={
                    <RoleProtectedRoute pageId="eSign"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ESignView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="calendarnews"
                  element={
                    <RoleProtectedRoute pageId="calendarnews"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><CalendarNews /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="supportteam"
                  element={
                    <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><ComingSoonView /></Suspense></ErrorBoundary>
                  }
                />
                <Route
                  path="urfx-ai"
                  element={
                    <RoleProtectedRoute pageId="ai_companion"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AiCompanion /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="trading-tool"
                  element={
                    <RoleProtectedRoute pageId="trading_tool"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TradingTool /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="trading-tool/symbols-hours"
                  element={
                    <RoleProtectedRoute pageId="trading_tool"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><SymbolsTradingHoursView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="trading-tool/trading-view"
                  element={
                    <RoleProtectedRoute pageId="trading_tool"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TradingViewToolView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="trading-tool/timezone-converter"
                  element={
                    <RoleProtectedRoute pageId="trading_tool"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><TimezoneConverterView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="trading-tool/lot-size-calculator"
                  element={
                    <RoleProtectedRoute pageId="trading_tool"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><LotSizeCalculatorView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="trading-tool/equity-simulator"
                  element={
                    <RoleProtectedRoute pageId="trading_tool"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><EquitySimulatorView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="leaderboard"
                  element={
                    <RoleProtectedRoute pageId="leaderboard"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><Leaderboard /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="analyticsview"
                  element={
                    <RoleProtectedRoute pageId="analyticsview"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AnalyticsView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="certificates"
                  element={
                    <RoleProtectedRoute pageId="certificates"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><CertificatesView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
                <Route
                  path="certificate-detail/:id"
                  element={
                    <ErrorBoundary><Suspense fallback={<LoadingSpinner />}><CertificateDetail /></Suspense></ErrorBoundary>
                  }
                />
                <Route
                  path="academy"
                  element={
                    <RoleProtectedRoute pageId="academy"><ErrorBoundary><Suspense fallback={<LoadingSpinner />}><AdminAcademyView /></Suspense></ErrorBoundary></RoleProtectedRoute>
                  }
                />
              </Route>

              {/*Common routes */}
              <Route path="/" element={<Layout />}>
                <Route path="" element={<LandingView />} />
                <Route path="programs" element={<SalesPage />} />
                <Route path="faqs" element={<Faqs />} />
                <Route path="about-us" element={<AboutUs />} />
                <Route path="contact-us" element={<SupportPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="velocity/*" element={<Navigate to="/" replace />} />
                {/* <Route path="faq" element={<FAQ />} /> */}
                <Route path="testimonials" element={<TestimonialView />} />
                <Route
                  path="admindb"
                  element={
                    <AdminDashboardView />
                  }
                />
                <Route
                  path="terms-&-conditions"
                  element={<TermsAndConditions />}
                />
                <Route path="privacy-policy" element={<PrivacyAndPolicy />} />
                <Route
                  path="become-a-partner"
                  element={
                    <BecomePartner />
                  }
                />
                <Route path="trading-rules" element={<TradingRules />} />
                <Route
                  path="/certificate"
                  element={
                    <CertificateVerficationView />
                  }
                />
                <Route path="pricing" element={<Pricing />} />
                <Route path="checkout" element={<ErrorBoundary><Suspense fallback={<LoadingSpinner />}><Checkout /></Suspense></ErrorBoundary>} />
                <Route path="promo" element={<PromoView />} />
                <Route path="performance" element={<ErrorBoundary><Suspense fallback={<LoadingSpinner />}><PerformanceView /></Suspense></ErrorBoundary>} />
              </Route>

              <Route path="/signup" element={<SignupView />} />
              <Route path="/login" element={<SigninView />} />
              <Route path="/email-verify" element={<VerifyEmailView />} />
              {/* Keep the legacy misspelled path working for already-sent emails. */}
              <Route path="/congrat-signup" element={<CongratForSignup />} />
              <Route path="/forgot-password" element={<ForgotPasswordView />} />
              <Route path="/reset-password" element={<ResetPasswordView />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              {/* /home is a duplicate of / — always 301-style client redirect */}
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/home/" element={<Navigate to="/" replace />} />
              {/* Blog is hosted on WordPress at /blogs — do not send /blog to home */}
              <Route path="/blog" element={<ExternalBlogRedirect />} />
              <Route path="/blogs" element={<ExternalBlogRedirect />} />
              <Route path="/blog/*" element={<ExternalBlogRedirect />} />
              {/*Redirect route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
        <ToastContainer
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick={false}
          pauseOnHover={true}
          draggable={true}
          theme="dark"
          transition={Flip}
        />
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
